/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Insights", description = "Hightouch's Campaign Intelligence product provides the Insights feature to measure how your audiences are performing.", lastUpdatedDate = new Date(1749772800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Insights",
        description: "Hightouch's Campaign Intelligence product provides the Insights feature to measure how your audiences are performing.",
        lastUpdatedDate: "Fri Jun 13 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Understanding the core concepts",
          "depth": 0,
          "id": "understanding-the-core-concepts"
        }, {
          "value": "Getting started with Insights",
          "depth": 0,
          "id": "getting-started-with-insights"
        }, {
          "value": "Metrics and properties",
          "depth": 0,
          "id": "metrics-and-properties"
        }, {
          "value": "Saved metrics",
          "depth": 1,
          "id": "saved-metrics"
        }, {
          "value": "Events and event properties",
          "depth": 1,
          "id": "events-and-event-properties"
        }, {
          "value": "Related model and related model properties",
          "depth": 1,
          "id": "related-model-and-related-model-properties"
        }, {
          "value": "User properties",
          "depth": 1,
          "id": "user-properties-1"
        }, {
          "value": "Traits",
          "depth": 1,
          "id": "traits-1"
        }, {
          "value": "Segmenting and grouping",
          "depth": 0,
          "id": "segmenting-and-grouping"
        }, {
          "value": "Segmenting",
          "depth": 1,
          "id": "segmenting"
        }, {
          "value": "Grouping",
          "depth": 1,
          "id": "grouping"
        }, {
          "value": "Visualizations",
          "depth": 0,
          "id": "visualizations"
        }, {
          "value": "Line charts",
          "depth": 1,
          "id": "line-charts"
        }, {
          "value": "Column charts",
          "depth": 1,
          "id": "column-charts"
        }, {
          "value": "Bar charts",
          "depth": 1,
          "id": "bar-charts"
        }, {
          "value": "Area charts",
          "depth": 1,
          "id": "area-charts"
        }, {
          "value": "Table view",
          "depth": 1,
          "id": "table-view"
        }, {
          "value": "Saving a chart",
          "depth": 0,
          "id": "saving-a-chart"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      ul: "ul",
      li: "li",
      strong: "strong",
      h4: "h4",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Campaign Intelligence is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Suppose you've just built a new ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "audience"
        }), " of users and deployed them to a campaign.\nYou are probably wondering: What actions are they taking? How has my audience reacted to that new campaign?"]
      }), "\n", _jsx(_components.p, {
        children: "The Insights feature enables you to view breakdowns of your users and define campaign success metrics to measure your audience against over time, all within Hightouch. You can track insights on metrics such as:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Revenue per country"
        }), "\n", _jsx(_components.li, {
          children: "Number of subscription sign-ups"
        }), "\n", _jsx(_components.li, {
          children: "Number of inactive users who've recently logged back in"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can then track how these metrics perform over time and compare them across audiences.\nInsights also works seamlessly with Hightouch ", _jsx(_components.a, {
          href: "/customer-studio/splits",
          children: "Splits"
        }), ". You can set up an A/B test within Hightouch, see how each split group performs, and determine a winner."]
      }), "\n", _jsxs(_components.p, {
        children: ["This doc provides step-by-step instructions for creating ", _jsx(_components.strong, {
          children: "Insight"
        }), " charts."]
      }), "\n", _jsx(_components.h2, {
        id: "understanding-the-core-concepts",
        children: "Understanding the core concepts"
      }), "\n", _jsx(_components.h4, {
        id: "custom-metrics-saved-metrics",
        children: "Custom metrics (“Saved metrics”)"
      }), "\n", _jsxs(_components.p, {
        children: ["You can define and save metrics using any ", _jsx(_components.a, {
          href: "/customer-studio/schema#related-models-and-events",
          children: _jsx(_components.strong, {
            children: "event"
          })
        }), " in your ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "data schema"
        }), ".\nMetrics are defined on a ", _jsx(_components.a, {
          href: "/customer-studio/schema#parent-models",
          children: "parent-model"
        }), " basis, and you can enable them on any audience built off the parent model."]
      }), "\n", _jsx(_components.h4, {
        id: "events-and-related-models",
        children: "Events and related models"
      }), "\n", _jsxs(_components.p, {
        children: ["Events and related models provide additional characteristics or actions to filter your ", _jsx(_components.a, {
          href: "/customer-studio/schema#parent-models",
          children: "parent-model"
        }), ". They're similar, but the key difference is that events require a timestamp.\nEvents and related models contain ", _jsx(_components.strong, {
          children: "properties"
        }), " — if you think of each instance as a row, properties are the columns."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.img, {
          src: "campaign-intel/insights/events-vs-properties.png",
          alt: "Event model SQL table example. Rows = Events. Columns = Event properties."
        }), "\n", _jsx(_components.img, {
          src: "campaign-intel/insights/related-vs-properites.png",
          alt: "Related model SQL table example. Rows = Related rows. Columns = Related properties."
        })]
      }), "\n", _jsx(_components.h4, {
        id: "user-properties",
        children: "User properties"
      }), "\n", _jsx(_components.p, {
        children: "Properties that are defined by your parent model. If you think of each user in your parent model as a row, user properties are the columns."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/parent-model-vs-properties.png",
          alt: "Parent model SQL table example. Rows = Users. Columns = User properties."
        })
      }), "\n", _jsx(_components.h4, {
        id: "traits",
        children: "Traits"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/traits",
          children: "Traits"
        }), " are custom columns added to your parent model."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/traits.png",
          alt: "Parent model SQL table with Trait example. Traits = Custom columns."
        })
      }), "\n", _jsx(_components.h2, {
        id: "getting-started-with-insights",
        children: "Getting started with Insights"
      }), "\n", _jsxs(_components.p, {
        children: ["Within the ", _jsx(_components.strong, {
          children: "Charts"
        }), " product, the ", _jsx(_components.strong, {
          children: "Insights"
        }), " tab allows you to create these charts. You can also view Insights charts that have already been created and saved."]
      }), "\n", _jsx(_components.h2, {
        id: "metrics-and-properties",
        children: "Metrics and properties"
      }), "\n", _jsx(_components.p, {
        children: "You can base an insight chart off of several different measurement options, and depending on the type you select, you can select an aggregation type, define a measurement window and add filters."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Aggregation type:"
          }), " Defines how individual instances are combined to form the metric – for example, the total number of rows, unique users, or the sum, count or average of a property."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Measurement window:"
          }), " Specifies the time period in which the selected metric must occur - for example, measuring an event from audience entry to 30 days after audience exit."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Filters:"
          }), " Narrow down the data included. Filters are based on the properties of the models you use – for example location, product type, or price."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "saved-metrics",
        children: "Saved metrics"
      }), "\n", _jsxs(_components.p, {
        children: ["Saved metrics already include an aggregation, so additional aggregations can't be applied within ", _jsx(_components.strong, {
          children: "Charts"
        }), ". However, you can change the measurement window and filters."]
      }), "\n", _jsx(_components.h3, {
        id: "events-and-event-properties",
        children: "Events and event properties"
      }), "\n", _jsx(_components.p, {
        children: "Different aggregations are available depending on whether an event property is included."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/event-aggregation.png",
          alt: "Table showing which aggregations work for Events and which work for Event properties."
        })
      }), "\n", _jsx(_components.h3, {
        id: "related-model-and-related-model-properties",
        children: "Related model and related model properties"
      }), "\n", _jsxs(_components.p, {
        children: ["Different aggregations are available depending on whether a ", _jsx(_components.a, {
          href: "/customer-studio/schema#related-models-and-events",
          children: "related model"
        }), " property is included."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/related-model-aggregation.png",
          alt: "Table showing which aggregations work for Related models and which work for Related model properties."
        })
      }), "\n", _jsx(_components.h3, {
        id: "user-properties-1",
        children: "User properties"
      }), "\n", _jsx(_components.p, {
        children: "You can use the property you select in an aggregation on the parent model. Different aggregations are available depending on whether the user property is included."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/parent-model-aggregation.png",
          alt: "Table showing which aggregations work for Related models and which work for Related model properties."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Additional filters cannot be applied, but the metric can be ", _jsx(_components.strong, {
          children: "segmented"
        }), " and ", _jsx(_components.strong, {
          children: "grouped"
        }), ", as detailed below."]
      }), "\n", _jsx(_components.h3, {
        id: "traits-1",
        children: "Traits"
      }), "\n", _jsxs(_components.p, {
        children: ["Traits have a per-user aggregation built in, so additional aggregations can't be applied within ", _jsx(_components.strong, {
          children: "Charts"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "segmenting-and-grouping",
        children: "Segmenting and grouping"
      }), "\n", _jsx(_components.h3, {
        id: "segmenting",
        children: "Segmenting"
      }), "\n", _jsx(_components.p, {
        children: "Segment data by one or more predefined Audiences, or create ad-hoc user segments, to focus the analysis on specific groups of users."
      }), "\n", _jsx(_components.h3, {
        id: "grouping",
        children: "Grouping"
      }), "\n", _jsx(_components.p, {
        children: "Group your data based on one or more selected properties or Traits (e.g., gender, location, brand loyalty). This allows you to compare the metric across different segments of users."
      }), "\n", _jsx(_components.h2, {
        id: "visualizations",
        children: "Visualizations"
      }), "\n", _jsx(_components.h3, {
        id: "line-charts",
        children: "Line charts"
      }), "\n", _jsx(_components.p, {
        children: "Used to view and compare data trends over-time or cumulatively, across a selected time-frame and interval (e.g., daily, weekly). Ideal for tracking changes over time and comparing multiple series."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Examples:"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "How many marketing emails did I send to this audience weekly over the last 60 days?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/big_chart_line_chart_1.png",
          alt: "Line chart showing how many marketing emails were sent to an audience over the last 60 days."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "How many marketing emails did I send to these two audiences weekly over the last 60 days?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/big_chart_line_chart_2.png",
          alt: "Line chart showing how many marketing emails were sent to two different audiences over the last 60 days."
        })
      }), "\n", _jsx(_components.h3, {
        id: "column-charts",
        children: "Column charts"
      }), "\n", _jsx(_components.p, {
        children: "View and compare aggregated values across categories using vertical bars. This can be done over-time or cumulatively, across a selected time-frame and interval (e.g., daily, weekly). Great for visual side-by-side comparisons."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Example: Across two audiences, how can I compare how many users from each audience have started a checkout process over the last 7 days?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/big_chart_column_chart.png",
          alt: "Column chart comparing the number of users who have started a checkout process from two different audiences over the last 7 days."
        })
      }), "\n", _jsx(_components.h3, {
        id: "bar-charts",
        children: "Bar charts"
      }), "\n", _jsx(_components.p, {
        children: "View and compare aggregated data across segments and groupings using horizontal bars for a given time frame. Best for top-N comparisons or summaries."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Example: How is my audience distributed by state?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/big_chart_bar_chart.png",
          alt: "Bar chart comparing the number of users that live in each state."
        })
      }), "\n", _jsx(_components.h3, {
        id: "area-charts",
        children: "Area charts"
      }), "\n", _jsx(_components.p, {
        children: "View changes in data volume over-time or cumulatively, across a selected time-frame and interval (e.g., daily, weekly). Useful for visualizing contribution trends and growth."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Example: What is the cumulative number of marketing emails sent to my audience over the last 30 days?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/big_chart_area_chart.png",
          alt: "Area chart showing the number of marketing emails sent to an audience over the last 30 days."
        })
      }), "\n", _jsx(_components.h3, {
        id: "table-view",
        children: "Table view"
      }), "\n", _jsx(_components.p, {
        children: "View aggregated data in rows, allowing for detailed comparison across values of a specific property for a given time-frame. Ideal for in-depth row-by-row analysis."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Example: How many users by state have received marketing emails over the last 60 days?"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/insights/big_chart_table_view.png",
          alt: "Table view comparing the number of users, per state, who have received marketing emails over the last 60 days."
        })
      }), "\n", _jsx(_components.h2, {
        id: "saving-a-chart",
        children: "Saving a chart"
      }), "\n", _jsx(_components.p, {
        children: "You can use the “Save chart” button to save any chart for future use. Once saved, the chart can be accessed from the Charts panel."
      })]
    });
  }
}
export default MDXContent;
